import React, { useState, useCallback } from 'react';
import { ActionIcon } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

// Reusable function that returns the complete header div
export const createHeaderWithHelp = (
  columnHeader: string, 
  helpText: string,
  onHelpClick: (helpText: string, position: { x: number; y: number }) => void
) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span>{columnHeader}</span>
      <ActionIcon
        size="xs"
        variant="subtle"
        color="gray"
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          onHelpClick(helpText, { 
            x: rect.left, 
            y: rect.bottom + 5 
          });
        }}
        title="Help"
      >
        <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
      </ActionIcon>
    </div>
  );
};

// Alternative: Reusable component that can be used directly in column definitions
export const HeaderWithHelp: React.FC<{
  columnHeader: string;
  helpText: string;
  onHelpClick: (helpText: string, position: { x: number; y: number }) => void;
}> = ({ columnHeader, helpText, onHelpClick }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span>{columnHeader}</span>
      <ActionIcon
        size="xs"
        variant="subtle"
        color="gray"
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          onHelpClick(helpText, { 
            x: rect.left, 
            y: rect.bottom + 5 
          });
        }}
        title="Help"
      >
        <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
      </ActionIcon>
    </div>
  );
};

// Custom hook for help panel functionality
export const useHelpPanel = () => {
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [helpContent, setHelpContent] = useState('');
  const [helpPosition, setHelpPosition] = useState({ x: 0, y: 0 });
  const [arrowDirection, setArrowDirection] = useState<'left' | 'right'>('right');

  const handleHelpClick = useCallback((helpText: string, position: { x: number; y: number }) => {
    // Adjust position if help panel would go off-screen
    const adjustedPosition = { ...position };
    let newArrowDirection: 'left' | 'right' = 'right';
    
    // Check if help panel would go off the right edge (assuming help panel width is ~300px)
    const helpPanelWidth = 300;
    const screenWidth = window.innerWidth;
    
    if (position.x + helpPanelWidth > screenWidth) {
      // Position the help panel to the left of the button instead
      adjustedPosition.x = position.x - helpPanelWidth;
      newArrowDirection = 'left';
    }
    
    setHelpPosition(adjustedPosition);
    setHelpContent(helpText);
    setArrowDirection(newArrowDirection);
    setShowHelpPanel(true);
  }, []);

  const closeHelpPanel = useCallback(() => {
    setShowHelpPanel(false);
  }, []);

  return {
    showHelpPanel,
    helpContent,
    helpPosition,
    arrowDirection,
    handleHelpClick,
    closeHelpPanel
  };
};
